import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { ApiServiceAdmin } from '../services/ApiServiceAdmin';

interface Props {
  isRTL: boolean;
  currentLanguage: string;
  canEdit?: boolean;
  isSuperUser?: boolean;
}

type SectionType = 'about' | 'project' | 'service' | 'event';
type LangKey = 'fr' | 'ar' | 'en';

interface LandingSection {
  id: number;
  section_type: SectionType;
  lang: LangKey;
  sort_order: number;
  title: string | null;
  description: string | null;
  detail_html: string | null;
  image_url: string | null;
  is_visible: 0 | 1;
}

interface EditContext {
  section: LandingSection | null;  // null = création
  stype: SectionType;
  lang: LangKey;
  isTitleEntry: boolean;           // sort_order=0 → titre de rubrique seulement
}

const SECTION_META: Record<SectionType, { icon: string; fr: string; ar: string }> = {
  about:   { icon: '🏛️', fr: 'À Propos',           ar: 'عن الجمعية' },
  event:   { icon: '🎉', fr: 'Événements',           ar: 'الأحداث والفعاليات' },
  project: { icon: '⭐', fr: 'Services Principaux', ar: 'الخدمات الرئيسية' },
  service: { icon: '📋', fr: 'Nos Services',         ar: 'خدماتنا' },
};

const LANGS: { key: LangKey; flag: string; label: string }[] = [
  { key: 'fr', flag: '🇫🇷', label: 'Français' },
  { key: 'ar', flag: 'Ar',  label: 'العربية' },
  { key: 'en', flag: '🇬🇧', label: 'English' },
];

const api = ApiServiceAdmin.getInstance();

function resolveImg(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith('/uploads') ? `${(api as any).baseUrl}${url}` : url;
}

/* ══════════════════════════════ STYLES ══════════════════════════════ */
const C = '#5e35b1';   // couleur principale
const S = {
  wrap:       { display: 'flex', flexDirection: 'column' as const, gap: 10 },
  notice:     (ok: boolean) => ({ padding: '8px 14px', borderRadius: 6, fontSize: 13, background: ok ? '#e8f5e9' : '#ffebee', color: ok ? '#2e7d32' : '#c62828' }),

  /* ── Accordéon ── */
  panel:      { border: '1px solid #e8e0f5', borderRadius: 10, overflow: 'hidden' as const, background: '#fff' },
  panelHd:    (open: boolean) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
    background: open ? C : '#faf7ff', cursor: 'pointer', userSelect: 'none' as const,
    borderBottom: open ? '1px solid #d4bfff' : 'none',
  }),
  panelTitle: (open: boolean) => ({ fontWeight: 700, fontSize: 14, color: open ? '#fff' : '#4a148c', flex: 1 }),
  panelCount: (open: boolean) => ({ fontSize: 11, color: open ? '#d4bfff' : '#aaa' }),
  chevron:    (open: boolean) => ({ fontSize: 12, color: open ? '#fff' : C, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }),
  body:       { padding: '10px 12px', display: 'flex', flexDirection: 'column' as const, gap: 8 },

  /* ── Onglets langue ── */
  langRow:    { display: 'flex', gap: 0, borderRadius: 7, overflow: 'hidden' as const, border: '1px solid #e8e0f5', alignSelf: 'flex-start' as const },
  langBtn:    (on: boolean) => ({
    padding: '5px 13px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: on ? 700 : 400,
    background: on ? C : '#fafafe', color: on ? '#fff' : '#888', borderRight: '1px solid #e8e0f5',
  }),

  /* ── Titre de section (sort_order=0) ── */
  secTitleRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, background: '#f3eeff' },
  secTitleLbl: { fontSize: 11, color: '#9c27b0', fontWeight: 600, whiteSpace: 'nowrap' as const },
  secTitleVal: { fontSize: 13, fontWeight: 700, color: '#4a148c', flex: 1 },

  /* ── Item row ── */
  item:       (visible: boolean) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px',
    borderRadius: 8, background: visible ? '#faf7ff' : '#f5f5f5',
    border: `1px solid ${visible ? '#e0d0ff' : '#e0e0e0'}`, opacity: visible ? 1 : 0.65,
  }),
  thumb:      { width: 36, height: 36, borderRadius: 5, objectFit: 'cover' as const, border: '1px solid #e0d0ff', flexShrink: 0 },
  thumbPh:    { width: 36, height: 36, borderRadius: 5, background: '#ede7f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, color: '#b39ddb' },
  itemTxt:    { flex: 1, minWidth: 0 },
  itemTitle:  { fontWeight: 600, fontSize: 13, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  itemDesc:   { fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },

  /* ── Petits boutons ── */
  tog:        (on: boolean) => ({ width: 32, height: 18, borderRadius: 9, border: 'none', cursor: 'pointer', background: on ? C : '#ccc', position: 'relative' as const, flexShrink: 0, transition: 'background .2s' }),
  togDot:     (on: boolean) => ({ position: 'absolute' as const, top: 1, left: on ? 15 : 1, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .2s' }),
  iconBtn:    (danger?: boolean) => ({
    padding: '4px 10px', border: `1px solid ${danger ? '#ffcdd2' : '#e0d0ff'}`, borderRadius: 6, flexShrink: 0,
    background: danger ? '#fff8f8' : '#faf7ff', color: danger ? '#e53935' : C, fontSize: 12, cursor: 'pointer', fontWeight: 600,
  }),
  addBtn:     {
    alignSelf: 'flex-start' as const, padding: '5px 13px', border: `1px dashed ${C}`,
    borderRadius: 6, background: 'transparent', color: C, fontSize: 12, cursor: 'pointer', marginTop: 2,
  },

  /* ── Modal ── */
  overlay:    { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal:      { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto' as const, padding: '18px 20px', display: 'flex', flexDirection: 'column' as const, gap: 12 },
  mHead:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  mTitle:     { fontSize: 14, fontWeight: 700, color: '#333', margin: 0 },
  mClose:     { border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#bbb' },
  divider:    { height: 1, background: '#f0e8ff' },
  field:      { display: 'flex', flexDirection: 'column' as const, gap: 3 },
  lbl:        { fontSize: 12, fontWeight: 600, color: C },
  hint:       { fontSize: 11, color: '#aaa' },
  inp:        { padding: '8px 11px', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' as const },
  ta:         (h: number) => ({ padding: '8px 11px', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' as const, minHeight: h, resize: 'vertical' as const }),
  imgRow:     { display: 'flex', gap: 8, alignItems: 'center' },
  upBtn:      { padding: '7px 12px', border: `1px solid ${C}`, borderRadius: 7, background: '#faf7ff', color: C, fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' as const, flexShrink: 0 },
  prevImg:    { width: 50, height: 36, objectFit: 'cover' as const, borderRadius: 4, border: '1px solid #eee', flexShrink: 0 },
  visRow:     { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 7, background: '#faf7ff', border: '1px solid #f0e8ff' },
  mFooter:    { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn:  { padding: '8px 18px', border: '1px solid #ddd', borderRadius: 7, background: '#fff', color: '#555', fontSize: 13, cursor: 'pointer' },
  saveBtn:    (dis: boolean) => ({ padding: '8px 22px', border: 'none', borderRadius: 7, background: dis ? '#b39ddb' : C, color: '#fff', fontSize: 13, cursor: dis ? 'not-allowed' : 'pointer', fontWeight: 700 }),
};

/* ── Prévisualisation fidèle au rendu public ── */
const HTML_RE = /<[a-z][\s\S]*>/i;

interface PreviewProps { sec: LandingSection; lbl: (fr: string, ar: string) => string; onClose: () => void; }
const SectionPreview: React.FC<PreviewProps> = ({ sec, lbl, onClose }) => {
  const dir = sec.lang === 'ar' ? 'rtl' : 'ltr';
  const PRIMARY = '#5e35b1';

  /* ── Rendu EVENT ── */
  if (sec.section_type === 'event') {
    const rawBg     = sec.image_url;
    const isCssBg   = rawBg ? /^(#|rgb|rgba|hsl|linear-gradient|radial-gradient)/i.test(rawBg) : false;
    const bgStyle   = !rawBg ? {}
      : isCssBg
        ? { background: rawBg }
        : { backgroundImage: `url(${rawBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' };
    const titleHtml  = sec.title ? HTML_RE.test(sec.title) : false;
    const descHtml   = sec.description ? HTML_RE.test(sec.description) : false;

    return (
      <div style={S.overlay} onClick={onClose}>
        <div style={{ width: '90%', maxWidth: 860, maxHeight: '90vh', overflowY: 'auto', borderRadius: 10, background: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,.35)' }} onClick={e => e.stopPropagation()}>
          {/* Barre titre preview */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f5f5f5', borderRadius: '10px 10px 0 0', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontSize: 12, color: '#888' }}>👁 {lbl('Rendu public — Événement', 'العرض العام — حدث')}</span>
            <button style={S.mClose} onClick={onClose}>✕</button>
          </div>
          {/* Rendu identique à App.tsx events */}
          <div style={{ ...bgStyle, fontFamily: 'inherit' }}>
            {sec.title && (
              titleHtml
                ? <div dir={dir} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sec.title) }} />
                : <h2 style={{ textAlign: 'center', fontSize: '2.5rem', color: 'rgb(69, 90, 100)', margin: 0 }}>{sec.title}</h2>
            )}
            {sec.description && (
              descHtml
                ? <div dir={dir} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sec.description) }} />
                : <p style={{ textAlign: 'center', fontSize: '1.05rem', lineHeight: '1.8', color: '#6C757D', margin: '16px auto 0', maxWidth: 800 }}>{sec.description}</p>
            )}
            {sec.detail_html && (
              <button style={{ display: 'block', margin: '20px auto', padding: '12px 28px', fontSize: '1rem', borderRadius: 5, border: `2px solid ${PRIMARY}`, background: 'transparent', color: PRIMARY, cursor: 'default' }}>
                {sec.lang === 'ar' ? 'اقرأ المزيد' : 'Lire la suite'}
              </button>
            )}
          </div>
          {/* Aperçu "Lire la suite" si rempli */}
          {sec.detail_html && (
            <>
              <div style={{ height: 1, background: '#eee', margin: '0 16px' }} />
              <div style={{ padding: '12px 16px' }}>
                <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600 }}>{lbl('Contenu de la fenêtre "Lire la suite"', 'محتوى نافذة "اقرأ المزيد"')}</span>
                <div dir={dir} style={{ marginTop: 8, fontSize: 14, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sec.detail_html) }} />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ── Rendu PROJECT / SERVICE (carte identique à la page) ── */
  const descHtml = sec.description ? HTML_RE.test(sec.description) : false;
  const imgSrc_  = resolveImg(sec.image_url);
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{ width: '90%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', borderRadius: 10, background: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,.35)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f5f5f5', borderRadius: '10px 10px 0 0', borderBottom: '1px solid #e0e0e0' }}>
          <span style={{ fontSize: 12, color: '#888' }}>👁 {lbl('Rendu public — Carte', 'العرض العام — بطاقة')}</span>
          <button style={S.mClose} onClick={onClose}>✕</button>
        </div>
        {/* Carte identique à App.tsx project/service */}
        <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '24px', boxShadow: 'inset 0 1px 0 #f0f0f0' }}>
          {imgSrc_ && <img src={imgSrc_} alt={sec.title || ''} style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 8, marginBottom: 16, display: 'block' }} onError={e => (e.currentTarget.style.display = 'none')} />}
          {sec.title && <h3 dir={dir} style={{ fontSize: '1.2rem', color: PRIMARY, marginBottom: 10, textAlign: 'center' }}>{sec.title}</h3>}
          {sec.description && (
            descHtml
              ? <div dir={dir} style={{ fontSize: '0.95rem', color: '#6C757D', lineHeight: 1.6, marginBottom: 14 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sec.description) }} />
              : <p dir={dir} style={{ fontSize: '0.95rem', color: '#6C757D', lineHeight: 1.6, marginBottom: 14, textAlign: 'center' }}>{sec.description}</p>
          )}
          {sec.detail_html && (
            <>
              <button style={{ display: 'block', margin: '0 auto 16px', padding: '8px 20px', fontSize: '0.9rem', borderRadius: 5, border: `1px solid ${PRIMARY}`, background: 'transparent', color: PRIMARY, cursor: 'default' }}>
                {sec.lang === 'ar' ? 'اقرأ المزيد' : 'Lire la suite'}
              </button>
              <div style={{ height: 1, background: '#eee', margin: '0 0 12px' }} />
              <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600 }}>{lbl('Contenu de la fenêtre "Lire la suite"', 'محتوى نافذة "اقرأ المزيد"')}</span>
              <div dir={dir} style={{ marginTop: 8, fontSize: 14, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sec.detail_html) }} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Ligne d'item réutilisable (project / service / event) ── */
interface ItemRowProps {
  sec: LandingSection;
  meta: { icon: string; fr: string; ar: string };
  canEdit: boolean;
  stype: SectionType;
  lang: LangKey;
  lbl: (fr: string, ar: string) => string;
  onEdit:    (ctx: EditContext) => void;
  onDelete:  (sec: LandingSection) => void;
  onToggle:  (sec: LandingSection) => void;
  onPreview: (sec: LandingSection) => void;
}
const ItemRow: React.FC<ItemRowProps> = ({ sec, meta, canEdit, stype, lang, lbl, onEdit, onDelete, onToggle, onPreview }) => (
  <div style={S.item(!!sec.is_visible)}>
    {resolveImg(sec.image_url)
      ? <img src={resolveImg(sec.image_url)!} alt={sec.title || ''} style={S.thumb} onError={e => (e.currentTarget.style.display = 'none')} />
      : <div style={S.thumbPh}>{meta.icon}</div>}
    <div style={S.itemTxt}>
      <div style={S.itemTitle}>{sec.title || lbl('(sans titre)', '(بدون عنوان)')}</div>
      {sec.description && <div style={S.itemDesc}>{sec.description.replace(/<[^>]+>/g, '').slice(0, 80)}…</div>}
    </div>
    {/* Bouton prévisualisation — visible si du contenu HTML/texte existe */}
    {(sec.description || sec.detail_html) && (
      <button
        style={{ ...S.iconBtn(), padding: '4px 8px', fontSize: 13 }}
        title={lbl('Prévisualiser le contenu', 'معاينة المحتوى')}
        onClick={() => onPreview(sec)}
      >
        👁
      </button>
    )}
    {canEdit && <>
      <button style={S.tog(!!sec.is_visible)} onClick={() => onToggle(sec)} title={sec.is_visible ? lbl('Masquer', 'إخفاء') : lbl('Afficher', 'إظهار')}>
        <span style={S.togDot(!!sec.is_visible)} />
      </button>
      <button style={S.iconBtn()} onClick={() => onEdit({ section: sec, stype, lang, isTitleEntry: false })}>✏️</button>
      <button style={S.iconBtn(true)} onClick={() => onDelete(sec)}>🗑</button>
    </>}
  </div>
);

/* ── Champ texte/HTML avec aperçu intégré ── */
interface FWPProps {
  label: string; hint: string; value: string; minHeight: number;
  placeholder: string; dir: 'ltr' | 'rtl';
  onChange: (v: string) => void;
}
const FieldWithPreview: React.FC<FWPProps> = ({ label, hint, value, minHeight, placeholder, dir, onChange }) => {
  const [preview, setPreview] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: C }}>{label}</label>
        {value && (
          <button
            type="button"
            onClick={() => setPreview(p => !p)}
            style={{ fontSize: 11, color: preview ? C : '#aaa', border: `1px solid ${preview ? C : '#ddd'}`, borderRadius: 5, padding: '2px 8px', background: preview ? '#f3eeff' : '#fff', cursor: 'pointer' }}
          >
            {preview ? '✕ Aperçu' : '👁 Aperçu'}
          </button>
        )}
      </div>
      <span style={{ fontSize: 11, color: '#aaa' }}>{hint}</span>
      {preview ? (
        <div
          style={{ padding: '8px 11px', border: '1px solid #e0d0ff', borderRadius: 7, minHeight, background: '#faf7ff', fontSize: 13, lineHeight: 1.6, direction: dir }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
        />
      ) : (
        <textarea
          style={{ padding: '8px 11px', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' as const, minHeight, resize: 'vertical' as const }}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          dir={dir}
        />
      )}
    </div>
  );
};

/* ══════════════════════════════ COMPOSANT ══════════════════════════════ */
export const LandingSectionsTab: React.FC<Props> = ({ isRTL, currentLanguage, canEdit = true, isSuperUser = false }) => {
  const lbl = (fr: string, ar: string) => currentLanguage === 'ar' ? ar : fr;

  const [sections,   setSections]   = useState<LandingSection[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [openPanel,  setOpenPanel]  = useState<SectionType | null>('about');
  const [activeLang, setActiveLang] = useState<Record<SectionType, LangKey>>({ about: 'fr', event: 'fr', project: 'fr', service: 'fr' });
  const [editCtx,    setEditCtx]    = useState<EditContext | null>(null);
  const [previewSec, setPreviewSec] = useState<LandingSection | null>(null);
  const [form,       setForm]       = useState<Partial<LandingSection>>({});
  const [saving,     setSaving]     = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [msg,        setMsg]        = useState<{ ok: boolean; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* ── Chargement ── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getLandingSections();
      if (res.success) setSections(res.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Helpers de recherche ── */
  const findTitle = (type: SectionType, lang: LangKey) =>
    sections.find(s => s.section_type === type && s.lang === lang && s.sort_order === 0) ?? null;

  const findAbout = (lang: LangKey) =>
    sections.find(s => s.section_type === 'about' && s.lang === lang) ?? null;

  const findItems = (type: SectionType, lang: LangKey) =>
    sections
      .filter(s => s.section_type === type && s.lang === lang && s.sort_order > 0)
      .sort((a, b) => a.sort_order - b.sort_order);

  /* ── Ouvrir formulaire ── */
  const openEdit = (ctx: EditContext) => {
    const sec = ctx.section;
    setEditCtx(ctx);
    setForm(sec ? {
      title:       sec.title || '',
      description: sec.description || '',
      detail_html: sec.detail_html || '',
      image_url:   sec.image_url || '',
      is_visible:  sec.is_visible,
    } : {
      title: '', description: '', detail_html: '', image_url: '',
      // Les entrées de config (sort_order=0) et les non-events sont visibles par défaut
      // Les nouveaux items events sont masqués par défaut
      is_visible: (ctx.stype === 'event' && !ctx.isTitleEntry) ? 0 : 1,
    });
    setMsg(null);
  };

  const closeEdit = () => { setEditCtx(null); setMsg(null); };

  /* ── Upload image ── */
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadLandingImage(file);
      if (res.success) setForm(f => ({ ...f, image_url: res.url }));
      else setMsg({ ok: false, text: lbl('Erreur upload', 'خطأ في رفع الصورة') });
    } catch { setMsg({ ok: false, text: lbl('Erreur upload', 'خطأ') }); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  /* ── Sauvegarder ── */
  const handleSave = async () => {
    const isEventBgEntry = editCtx?.isTitleEntry && editCtx?.stype === 'event';
    if (!isEventBgEntry && !form.title?.trim()) { setMsg({ ok: false, text: lbl('Le titre est requis', 'العنوان مطلوب') }); return; }
    setSaving(true); setMsg(null);
    try {
      const ctx = editCtx!;
      const nextOrder = ctx.isTitleEntry ? 0
        : findItems(ctx.stype, ctx.lang).reduce((m, s) => Math.max(m, s.sort_order), 0) + 1;

      const payload = {
        ...form,
        section_type: ctx.stype,
        lang:         ctx.lang,
        sort_order:   ctx.section ? ctx.section.sort_order : nextOrder,
        // L'entrée de fond de section doit toujours être visible pour être retournée par l'API publique
        ...(isEventBgEntry ? { is_visible: 1 } : {}),
      };
      const res = ctx.section?.id
        ? await api.updateLandingSection(ctx.section.id, payload)
        : await api.createLandingSection(payload);

      if (res.success) {
        setMsg({ ok: true, text: lbl('Enregistré ✓', 'تم الحفظ ✓') });
        await load();
        setTimeout(closeEdit, 700);
      } else setMsg({ ok: false, text: res.message || lbl('Erreur', 'خطأ') });
    } catch (err: any) {
      setMsg({ ok: false, text: err.message || lbl('Erreur', 'خطأ') });
    } finally { setSaving(false); }
  };

  /* ── Supprimer un item ── */
  const handleDelete = async (sec: LandingSection) => {
    if (!window.confirm(lbl(`Supprimer "${sec.title}" ?`, `حذف "${sec.title}" ؟`))) return;
    try {
      await api.deleteLandingSection(sec.id);
      await load();
    } catch { setMsg({ ok: false, text: lbl('Erreur suppression', 'خطأ في الحذف') }); }
  };

  /* ── Basculer visibilité ── */
  const toggleVis = async (sec: LandingSection) => {
    try {
      await api.updateLandingSection(sec.id, { is_visible: sec.is_visible ? 0 : 1 });
      await load();
    } catch { setMsg({ ok: false, text: lbl('Erreur', 'خطأ') }); }
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>⏳</div>;

  return (
    <div style={S.wrap} dir={isRTL ? 'rtl' : 'ltr'}>
      {msg && !editCtx && <div style={S.notice(msg.ok)}>{msg.text}</div>}

      {/* ══════════ Panneaux ══════════ */}
      {(Object.keys(SECTION_META) as SectionType[]).filter(stype => isSuperUser || stype === 'event').map(stype => {
        const meta   = SECTION_META[stype];
        const isOpen = openPanel === stype;
        const lang   = activeLang[stype];
        const itemCount = stype === 'about' ? (findAbout(lang) ? 1 : 0) : findItems(stype, lang).length;

        return (
          <div key={stype} style={S.panel}>
            {/* En-tête accordéon */}
            <div style={S.panelHd(isOpen)} onClick={() => setOpenPanel(isOpen ? null : stype)}>
              <span style={{ fontSize: 18 }}>{meta.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={S.panelTitle(isOpen)}>{lbl(meta.fr, meta.ar)}</div>
                {isOpen && (
                  <div style={S.panelCount(isOpen)}>
                    {stype === 'about'
                      ? lbl('Section unique par langue', 'قسم واحد لكل لغة')
                      : lbl(`${itemCount} élément(s) — onglets de langue ci-dessous`, `${itemCount} عنصر — علامات اللغة أدناه`)}
                  </div>
                )}
              </div>
              <span style={S.chevron(isOpen)}>▾</span>
            </div>

            {/* Corps */}
            {isOpen && (
              <div style={S.body}>
                {/* Sélecteur de langue */}
                <div style={S.langRow}>
                  {LANGS.map(({ key, flag, label }, i) => (
                    <button
                      key={key}
                      style={{ ...S.langBtn(lang === key), borderRight: i < LANGS.length - 1 ? '1px solid #e8e0f5' : 'none' }}
                      onClick={() => setActiveLang(prev => ({ ...prev, [stype]: key }))}
                    >
                      {flag} {label}
                    </button>
                  ))}
                </div>

                {/* ── CAS 1 : À PROPOS (entrée unique par langue) ── */}
                {stype === 'about' && (() => {
                  const sec = findAbout(lang);
                  if (!sec) return (
                    <div style={{ textAlign: 'center', padding: '12px 0', color: '#bbb', fontSize: 12 }}>
                      {lbl('Aucun contenu.', 'لا يوجد محتوى.')}{' '}
                      {canEdit && <button style={S.addBtn} onClick={() => openEdit({ section: null, stype, lang, isTitleEntry: false })}>+ {lbl('Créer', 'إنشاء')}</button>}
                    </div>
                  );
                  return (
                    <div style={S.item(!!sec.is_visible)}>
                      <div style={S.thumbPh}>{meta.icon}</div>
                      <div style={S.itemTxt}>
                        <div style={S.itemTitle}>{sec.title || lbl('(sans titre)', '(بدون عنوان)')}</div>
                        {sec.description && <div style={S.itemDesc}>{sec.description.slice(0, 100)}…</div>}
                      </div>
                      {sec.detail_html && <span title={lbl('Contient un texte "Lire la suite"', 'يحتوي على نص "اقرأ المزيد"')} style={{ fontSize: 14 }}>📖</span>}
                      {canEdit && <>
                        <button style={S.tog(!!sec.is_visible)} onClick={() => toggleVis(sec)} title={sec.is_visible ? lbl('Masquer', 'إخفاء') : lbl('Afficher', 'إظهار')}>
                          <span style={S.togDot(!!sec.is_visible)} />
                        </button>
                        <button style={S.iconBtn()} onClick={() => openEdit({ section: sec, stype, lang, isTitleEntry: false })}>✏️ {lbl('Modifier', 'تعديل')}</button>
                      </>}
                    </div>
                  );
                })()}

                {/* ── CAS 2 : ÉVÉNEMENTS ── */}
                {stype === 'event' && (() => {
                  const bgEntry = findTitle(stype, lang);   // sort_order=0 → fond global
                  const items   = findItems(stype, lang);
                  return (
                    <>
                      {/* Fond global de la section */}
                      <div style={S.secTitleRow}>
                        <span style={S.secTitleLbl}>{lbl('Fond de la section', 'خلفية القسم')}</span>
                        {bgEntry?.image_url
                          ? <span style={{ ...S.secTitleVal, fontFamily: 'monospace', fontSize: 11 }}>{bgEntry.image_url}</span>
                          : <span style={{ ...S.secTitleVal, color: '#bbb', fontStyle: 'italic' }}>{lbl('(aucun)', '(لا شيء)')}</span>}
                        {canEdit && (
                          <button style={{ ...S.iconBtn(), padding: '3px 8px', fontSize: 11 }}
                            onClick={() => openEdit({ section: bgEntry, stype, lang, isTitleEntry: true })}>
                            {bgEntry ? lbl('✏️ Modifier', '✏️ تعديل') : lbl('+ Définir', '+ تحديد')}
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 6, background: '#fff8e1', border: '1px solid #ffe082', fontSize: 11, color: '#f57c00' }}>
                        <span>💡</span>
                        <span>{lbl('Désactivés par défaut. Activez chaque événement quand il est d\'actualité.', 'مُعطَّلة افتراضياً. فعّل كل حدث عندما يكون حالياً.')}</span>
                      </div>
                      {items.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '8px 0', color: '#bbb', fontSize: 12 }}>
                          {lbl('Aucun événement. Cliquez "+ Ajouter" pour créer le premier.', 'لا توجد أحداث. انقر "+ إضافة" لإنشاء الأول.')}
                        </div>
                      )}
                      {items.map(sec => <ItemRow key={sec.id} sec={sec} meta={meta} canEdit={canEdit} stype={stype} lang={lang} lbl={lbl} onEdit={openEdit} onDelete={handleDelete} onToggle={toggleVis} onPreview={setPreviewSec} />)}
                      {canEdit && (
                        <button style={S.addBtn} onClick={() => openEdit({ section: null, stype, lang, isTitleEntry: false })}>
                          + {lbl('Ajouter un événement', 'إضافة حدث')}
                        </button>
                      )}
                    </>
                  );
                })()}

                {/* ── CAS 3 : PROJECT / SERVICE ── */}
                {(stype === 'project' || stype === 'service') && (() => {
                  const titleEntry = findTitle(stype, lang);
                  const items      = findItems(stype, lang);
                  return (
                    <>
                      <div style={S.secTitleRow}>
                        <span style={S.secTitleLbl}>{lbl('Titre de la rubrique', 'عنوان القسم')}</span>
                        {titleEntry
                          ? <span style={S.secTitleVal}>{titleEntry.title}</span>
                          : <span style={{ ...S.secTitleVal, color: '#bbb', fontStyle: 'italic' }}>{lbl('(non défini)', '(غير محدد)')}</span>}
                        {canEdit && (
                          <button style={{ ...S.iconBtn(), padding: '3px 8px', fontSize: 11 }}
                            onClick={() => openEdit({ section: titleEntry, stype, lang, isTitleEntry: true })}>
                            {titleEntry ? lbl('✏️ Renommer', '✏️ تعديل') : lbl('+ Définir', '+ تحديد')}
                          </button>
                        )}
                      </div>
                      {items.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '8px 0', color: '#bbb', fontSize: 12 }}>
                          {lbl('Aucun élément.', 'لا توجد عناصر.')}
                        </div>
                      )}
                      {items.map(sec => <ItemRow key={sec.id} sec={sec} meta={meta} canEdit={canEdit} stype={stype} lang={lang} lbl={lbl} onEdit={openEdit} onDelete={handleDelete} onToggle={toggleVis} onPreview={setPreviewSec} />)}
                      {canEdit && (
                        <button style={S.addBtn} onClick={() => openEdit({ section: null, stype, lang, isTitleEntry: false })}>
                          + {lbl('Ajouter un élément', 'إضافة عنصر')}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })}

      {/* ══════════ Overlay prévisualisation — rendu fidèle à la page publique ══════════ */}
      {previewSec && <SectionPreview sec={previewSec} lbl={lbl} onClose={() => setPreviewSec(null)} />}

      {/* ══════════ Modal d'édition ══════════ */}
      {editCtx && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) closeEdit(); }}>
          <div style={S.modal}>
            {/* En-tête */}
            <div style={S.mHead}>
              <h3 style={S.mTitle}>
                {SECTION_META[editCtx.stype].icon}{' '}
                {editCtx.isTitleEntry && editCtx.stype === 'event'
                  ? lbl('Fond de la section', 'خلفية القسم')
                  : editCtx.isTitleEntry
                    ? lbl('Titre de la rubrique', 'عنوان القسم')
                    : (editCtx.section ? lbl('Modifier', 'تعديل') : lbl('Nouvel élément', 'عنصر جديد'))}
                {' — '}
                {LANGS.find(l => l.key === editCtx.lang)?.flag} {LANGS.find(l => l.key === editCtx.lang)?.label}
              </h3>
              <button style={S.mClose} onClick={closeEdit}>✕</button>
            </div>
            <div style={S.divider} />

            {msg && <div style={S.notice(msg.ok)}>{msg.text}</div>}

            {/* Fond de section (uniquement pour isTitleEntry event) */}
            {editCtx.isTitleEntry && editCtx.stype === 'event' ? (
              <div style={S.field}>
                <label style={S.lbl}>{lbl('Fond de la section', 'خلفية القسم')}</label>
                <span style={S.hint}>{lbl('Couleur CSS (#hex, rgb…), dégradé (linear-gradient…) ou URL d\'image', 'لون CSS أو تدرج أو رابط صورة')}</span>
                <input
                  style={S.inp} value={form.image_url || ''}
                  onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                  placeholder="#F8F9FA  ou  linear-gradient(135deg,#667eea,#764ba2)  ou  https://…"
                  dir="ltr"
                  autoFocus
                />
                {/* Aperçu du fond */}
                {form.image_url && (
                  <div style={{
                    height: 40, borderRadius: 6, marginTop: 4, border: '1px solid #ddd',
                    ...(/^(#|rgb|rgba|hsl|linear-gradient|radial-gradient)/i.test(form.image_url)
                      ? { background: form.image_url }
                      : { backgroundImage: `url(${form.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }),
                  }} />
                )}
              </div>
            ) : (
              /* Titre (project / service / about / event item) */
              <div style={S.field}>
                <label style={S.lbl}>{editCtx.isTitleEntry ? lbl('Titre de la rubrique', 'عنوان القسم') : lbl('Titre *', 'العنوان *')}</label>
                <input
                  style={S.inp} value={form.title || ''}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder={editCtx.isTitleEntry ? lbl('Ex : Nos Services Principaux', 'مثال: الخدمات الرئيسية') : lbl('Titre affiché sur la page', 'العنوان الظاهر')}
                  dir={editCtx.lang === 'ar' ? 'rtl' : 'ltr'}
                  autoFocus
                />
              </div>
            )}

            {/* Champs affichés seulement pour les vrais items (pas les titres de rubrique) */}
            {!editCtx.isTitleEntry && (<>
              {/* Texte de présentation */}
              <FieldWithPreview
                label={lbl('Texte de présentation', 'نص التعريف')}
                hint={lbl('Texte ou HTML — affiché sur la carte / la page d\'accueil', 'نص أو HTML — يُعرض على البطاقة والصفحة الرئيسية')}
                value={form.description || ''}
                onChange={v => setForm(f => ({ ...f, description: v }))}
                minHeight={76}
                dir={editCtx.lang === 'ar' ? 'rtl' : 'ltr'}
                placeholder={lbl('Quelques phrases de présentation…', 'بضع جمل تعريفية…')}
              />

              {/* Texte détaillé */}
              <FieldWithPreview
                label={lbl('Texte détaillé  —  bouton "Lire la suite"', 'نص تفصيلي  —  زر "اقرأ المزيد"')}
                hint={lbl('Texte ou HTML — optionnel. Vide = bouton masqué.', 'نص أو HTML — اختياري. فارغ = إخفاء الزر.')}
                value={form.detail_html || ''}
                onChange={v => setForm(f => ({ ...f, detail_html: v }))}
                minHeight={110}
                dir={editCtx.lang === 'ar' ? 'rtl' : 'ltr'}
                placeholder={lbl('Contenu qui s\'affiche en fenêtre au clic…', 'المحتوى الذي يظهر عند النقر…')}
              />

              {/* Image */}
              <div style={S.field}>
                <label style={S.lbl}>{lbl('Image / Photo', 'صورة')}</label>
                <div style={S.imgRow}>
                  <input
                    style={{ ...S.inp, flex: 1 }} value={form.image_url || ''}
                    onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                    placeholder={lbl('URL ou choisir un fichier →', 'رابط أو اختر ملفاً ←')}
                    dir="ltr"
                  />
                  <button style={S.upBtn} onClick={() => fileRef.current?.click()} disabled={uploading}>
                    {uploading ? '⏳' : lbl('📤 Choisir', '📤 اختيار')}
                  </button>
                  {form.image_url && (
                    <img src={resolveImg(form.image_url) || form.image_url} alt="" style={S.prevImg}
                      onError={e => (e.currentTarget.style.display = 'none')} />
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
              </div>

              {/* Visibilité */}
              <div style={S.visRow}>
                <span style={{ fontSize: 13, color: '#555', flex: 1 }}>
                  {lbl('Afficher cet élément sur la page d\'accueil', 'إظهار هذا العنصر في الصفحة الرئيسية')}
                </span>
                <button style={S.tog(!!form.is_visible)} onClick={() => setForm(f => ({ ...f, is_visible: f.is_visible ? 0 : 1 }))}>
                  <span style={S.togDot(!!form.is_visible)} />
                </button>
                <span style={{ fontSize: 12, color: form.is_visible ? C : '#aaa', fontWeight: 600 }}>
                  {form.is_visible ? lbl('Visible', 'ظاهر') : lbl('Masqué', 'مخفي')}
                </span>
              </div>
            </>)}

            <div style={S.divider} />

            {/* Footer */}
            <div style={S.mFooter}>
              <button style={S.cancelBtn} onClick={closeEdit}>{lbl('Annuler', 'إلغاء')}</button>
              <button style={S.saveBtn(saving)} onClick={handleSave} disabled={saving}>
                {saving ? lbl('Enregistrement…', 'جارٍ الحفظ…') : lbl('💾 Enregistrer', '💾 حفظ')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
